import React, { useState, useEffect, useMemo, useCallback } from "react";
import { Link, useNavigate, useLocation } from "react-router-dom";
import { useDispatch, useSelector } from "react-redux";
import { logoutUser } from "../actions/AuthActions";
import { 
  Menu, 
  X, 
  LayoutDashboard, 
  Calendar, 
  MapPin, 
  LogOut, 
  Clock,
  Truck,
  Navigation,
  User,
  Bell,
  ChevronDown,
  ChevronLeft,
  Car,
  ClipboardList
} from "lucide-react";
import { motion, AnimatePresence } from "framer-motion";
import dockyardImg from "../assets/dockyard.png";
import LocationIndicator from "./LocationIndicator";
import GPSEnableNotification from "./GPSEnableNotification";

const DriverLayout = ({ children }) => {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const location = useLocation();
  const { userDetails, loading } = useSelector((state) => state.auth || { loading: false });
  
  // State
  const [sidebarOpen, setSidebarOpen] = useState(false);
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const [showUserMenu, setShowUserMenu] = useState(false);
  const [currentTime, setCurrentTime] = useState(new Date());

  // Navigation items for Driver
  const navItems = [
    { icon: LayoutDashboard, label: "Dashboard", path: "/driver/dashboard" },
    { icon: Calendar, label: "Scheduled Bookings", path: "/driver/scheduled-bookings" },
    { icon: MapPin, label: "My Trips", path: "/driver/my-trips" },
    { icon: Navigation, label: "Live Tracking", path: "/driver/tracking" },
    { icon: User, label: "Profile", path: "/driver/profile" },
  ];

  // Update current time
  useEffect(() => {
    const timer = setInterval(() => setCurrentTime(new Date()), 1000);
    return () => clearInterval(timer);
  }, []);

  // Close mobile menu on resize
  useEffect(() => {
    const handleResize = () => {
      if (window.innerWidth >= 768) {
        setMobileMenuOpen(false);
      }
    };
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  // Close with Escape key
  useEffect(() => {
    const handleEscKey = (e) => {
      if (e.key === 'Escape' && mobileMenuOpen) {
        setMobileMenuOpen(false);
      }
    };
    window.addEventListener('keydown', handleEscKey);
    return () => window.removeEventListener('keydown', handleEscKey);
  }, [mobileMenuOpen]);

  // Profile image URL
  const profileImageUrl = userDetails?.ServiceNo
    ? `https://aeroviewbackend.dockyardsoftware.com/User/GetUserImg?Sno=${userDetails.ServiceNo}&t=${Date.now()}`
    : `https://ui-avatars.com/api/?name=${encodeURIComponent(userDetails?.Name || "Driver")}&background=1e293b&color=fff&size=96&bold=true`;

  const handleLogout = () => {
    if (window.confirm("Are you sure you want to log out?")) {
      dispatch(logoutUser());
      navigate("/login");
    }
  };

  const handleNavigation = (path) => {
    navigate(path);
    setMobileMenuOpen(false);
    setShowUserMenu(false);
  };

  const isActive = (path) => location.pathname === path;

  // Lock body scroll and add class when mobile menu is open
  useEffect(() => {
    if (mobileMenuOpen) {
      document.body.style.overflow = 'hidden';
      document.body.style.position = 'fixed';
      document.body.style.width = '100%';
      document.body.classList.add('mobile-drawer-open');
      
      // Force hide map elements by adding style to Leaflet container
      const mapContainer = document.querySelector('.leaflet-container');
      if (mapContainer) {
        mapContainer.style.setProperty('z-index', '0', 'important');
        mapContainer.style.setProperty('pointer-events', 'none', 'important');
      }
      
      // Hide all Leaflet controls
      const leafletControls = document.querySelectorAll('.leaflet-control-container, .leaflet-control-zoom, .leaflet-control-attribution');
      leafletControls.forEach(control => {
        control.style.setProperty('z-index', '0', 'important');
        control.style.setProperty('opacity', '0.3', 'important');
        control.style.setProperty('pointer-events', 'none', 'important');
      });
      
    } else {
      document.body.style.overflow = 'unset';
      document.body.style.position = 'unset';
      document.body.style.width = 'unset';
      document.body.classList.remove('mobile-drawer-open');
      
      // Restore map elements
      const mapContainer = document.querySelector('.leaflet-container');
      if (mapContainer) {
        mapContainer.style.removeProperty('z-index');
        mapContainer.style.removeProperty('pointer-events');
      }
      
      const leafletControls = document.querySelectorAll('.leaflet-control-container, .leaflet-control-zoom, .leaflet-control-attribution');
      leafletControls.forEach(control => {
        control.style.removeProperty('z-index');
        control.style.removeProperty('opacity');
        control.style.removeProperty('pointer-events');
      });
    }
    return () => {
      document.body.style.overflow = 'unset';
      document.body.style.position = 'unset';
      document.body.style.width = 'unset';
      document.body.classList.remove('mobile-drawer-open');
    };
  }, [mobileMenuOpen]);

  const currentTimeFormatted = useMemo(() => 
    currentTime.toLocaleTimeString('en-US', { hour: '2-digit', minute: '2-digit' }),
    [currentTime]
  );

  return (
    <div className="min-h-screen bg-gradient-to-br from-gray-50 to-gray-100">
      {/* ================= Glass-morphism Sidebar ================= */}
      <aside 
        className={`fixed left-0 top-0 h-full z-20 backdrop-blur-xl bg-white/80 shadow-xl transition-all duration-300 hidden md:block
          border-r border-white/30
          ${sidebarOpen ? "w-56" : "w-16"}`}
        style={{ paddingTop: 'env(safe-area-inset-top)' }}
      >
        {/* Logo Section - Compact */}
        <div className={`flex items-center ${sidebarOpen ? "justify-between px-3" : "justify-center"} py-3 border-b border-gray-200/50`}>
          {sidebarOpen ? (
            <>
              <div className="flex items-center gap-2">
                <div className="w-7 h-7 bg-gradient-to-br from-gray-800 to-gray-900 rounded-lg flex items-center justify-center shadow-md">
                  <Truck size={14} className="text-white" />
                </div>
                <span className="text-sm font-semibold bg-gradient-to-r from-gray-800 to-gray-600 bg-clip-text text-transparent">AeroView</span>
              </div>
              <button
                onClick={() => setSidebarOpen(false)}
                className="p-1 hover:bg-white/50 rounded-lg transition"
              >
                <ChevronLeft size={14} className="text-gray-500" />
              </button>
            </>
          ) : (
            <button
              onClick={() => setSidebarOpen(true)}
              className="p-2 hover:bg-white/50 rounded-lg transition"
            >
              <Truck size={18} className="text-gray-700" />
            </button>
          )}
        </div>

        {/* Navigation Items - Compact */}
        <nav className="flex flex-col h-full p-2">
          <div className="flex-1 space-y-1 mt-3">
            {navItems.map((item, index) => (
              <button
                key={index}
                onClick={() => handleNavigation(item.path)}
                className={`w-full flex items-center gap-3 px-2 py-2 rounded-lg transition-all duration-200 text-sm ${
                  isActive(item.path)
                    ? "bg-gray-900/90 backdrop-blur-sm text-white shadow-md"
                    : "text-gray-600 hover:bg-white/50 hover:backdrop-blur-sm"
                } ${!sidebarOpen && "justify-center"}`}
                title={!sidebarOpen ? item.label : undefined}
              >
                <item.icon size={sidebarOpen ? 16 : 18} />
                {sidebarOpen && <span className="truncate text-xs">{item.label}</span>}
              </button>
            ))}
          </div>
          
          {/* Logout Button */}
          <button
            onClick={handleLogout}
            disabled={loading}
            className={`flex items-center gap-3 px-2 py-2 rounded-lg text-red-600 hover:bg-red-50/80 transition text-sm mt-2 ${
              !sidebarOpen && "justify-center"
            }`}
            title={!sidebarOpen ? "Logout" : undefined}
          >
            <LogOut size={sidebarOpen ? 16 : 18} />
            {sidebarOpen && <span className="text-xs">{loading ? "..." : "Logout"}</span>}
          </button>
        </nav>
      </aside>

      {/* ================= Top Header ================= */}
      <header className="fixed top-0 right-0 left-0 z-30 bg-white/80 backdrop-blur-md border-b border-gray-200/50 shadow-sm safe-area-header">
        <div className="flex items-center justify-between px-3 sm:px-5 py-2.5">
          {/* Left Section */}
          <div className="flex items-center gap-2 sm:gap-3">
            {/* Mobile menu button */}
            <button
              onClick={() => setMobileMenuOpen(true)}
              className="md:hidden p-2 hover:bg-gray-100 rounded-lg transition"
            >
              <Menu size={18} className="text-gray-600" />
            </button>
            
            {/* Desktop sidebar toggle */}
            <button
              onClick={() => setSidebarOpen(!sidebarOpen)}
              className="hidden md:block p-2 hover:bg-gray-100/80 rounded-lg transition"
            >
              <Menu size={16} className="text-gray-600" />
            </button>
            
            {/* Logo and Title */}
            <div className="flex items-center gap-2">
              <div className="w-7 h-7 bg-gradient-to-br from-gray-800 to-gray-900 rounded-lg flex items-center justify-center">
                <Truck size={12} className="text-white" />
              </div>
              <div className="hidden sm:block">
                <h1 className="text-sm font-bold text-gray-800">Aero View</h1>
                <p className="text-xs text-gray-500">Driver Portal</p>
              </div>
            </div>
          </div>
          
          {/* Right Section */}
          <div className="flex items-center gap-2 sm:gap-3">
            {/* Location Indicator */}
            <div className="hidden sm:block">
              <LocationIndicator />
            </div>
            
            {/* GPS Notification */}
            <GPSEnableNotification />
            
            {/* Time Display */}
            <div className="hidden md:flex items-center gap-1 text-xs text-gray-500 bg-gray-100/80 px-2.5 py-1 rounded-full backdrop-blur-sm">
              <Clock size={11} />
              {currentTimeFormatted}
            </div>
            
            {/* Notifications */}
            <button className="relative p-1.5 hover:bg-gray-100 rounded-lg transition">
              <Bell size={16} className="text-gray-600" />
              <span className="absolute top-1 right-1 w-1.5 h-1.5 bg-red-500 rounded-full"></span>
            </button>
            
            {/* User Menu */}
            <div className="relative">
              <button
                onClick={() => setShowUserMenu(!showUserMenu)}
                className="flex items-center gap-2 pl-2 border-l border-gray-200 hover:bg-gray-50 rounded-lg transition p-1"
                disabled={loading}
              >
                <img
                  src={profileImageUrl}
                  alt={userDetails?.Name || "Driver"}
                  className="w-7 h-7 rounded-full object-cover border border-gray-200"
                />
                <span className="text-xs font-medium text-gray-700 hidden sm:inline">
                  {userDetails?.Name?.split(" ")[0] || "Driver"}
                </span>
                <ChevronDown size={12} className="text-gray-400 hidden sm:block" />
              </button>
              
              {/* Dropdown Menu */}
              {showUserMenu && (
                <>
                  <div className="fixed inset-0 z-40" onClick={() => setShowUserMenu(false)} />
                  <div className="absolute right-0 mt-2 w-52 bg-white/95 backdrop-blur-md rounded-xl shadow-lg border border-gray-100 py-1 z-50">
                    <div className="px-3 py-2 border-b border-gray-100">
                      <p className="text-xs font-semibold text-gray-900">{userDetails?.Name}</p>
                      <p className="text-xs text-gray-500 mt-0.5">{userDetails?.role || "Driver"}</p>
                      <p className="text-xs text-gray-400 font-mono mt-0.5">{userDetails?.ServiceNo}</p>
                    </div>
                    <button
                      onClick={() => {
                        setShowUserMenu(false);
                        navigate("/driver/profile");
                      }}
                      className="w-full text-left px-3 py-2 text-xs text-gray-700 hover:bg-gray-50 transition flex items-center gap-2"
                    >
                      <User size={12} />
                      Profile Settings
                    </button>
                    <button
                      onClick={handleLogout}
                      disabled={loading}
                      className="w-full text-left px-3 py-2 text-xs text-red-600 hover:bg-red-50 transition flex items-center gap-2"
                    >
                      <LogOut size={12} />
                      {loading ? "Logging out..." : "Logout"}
                    </button>
                  </div>
                </>
              )}
            </div>
          </div>
        </div>
      </header>

      {/* ================= Mobile Glass Drawer - COMPLETELY REWORKED ================= */}
      <AnimatePresence>
        {mobileMenuOpen && (
          <>
            {/* Dark overlay - HIGH z-index */}
            <motion.div
              key="overlay"
              initial={{ opacity: 0 }}
              animate={{ opacity: 0.6 }}
              exit={{ opacity: 0 }}
              className="fixed inset-0 bg-black md:hidden"
              style={{ zIndex: 9998 }}
              onClick={() => setMobileMenuOpen(false)}
            />

            {/* Drawer - HIGHEST z-index */}
            <motion.aside
              key="drawer"
              initial={{ x: "-100%" }}
              animate={{ x: 0 }}
              exit={{ x: "-100%" }}
              transition={{ type: "spring", stiffness: 300, damping: 30 }}
              className="md:hidden fixed top-0 left-0 h-screen w-64 bg-white shadow-2xl"
              style={{ 
                paddingTop: 'env(safe-area-inset-top)',
                zIndex: 9999,
                position: 'fixed',
                overflowY: 'auto'
              }}
            >
              <div className="flex flex-col h-full">
                {/* Header */}
                <div className="flex items-center justify-between p-4 border-b border-gray-100 bg-gradient-to-r from-gray-50 to-white">
                  <div className="flex items-center gap-2">
                    <div className="w-8 h-8 bg-gradient-to-br from-gray-800 to-gray-900 rounded-xl flex items-center justify-center">
                      <Truck size={14} className="text-white" />
                    </div>
                    <div>
                      <span className="text-sm font-semibold text-gray-900">Aero View</span>
                      <p className="text-xs text-gray-500">Driver Portal</p>
                    </div>
                  </div>
                  <button
                    onClick={() => setMobileMenuOpen(false)}
                    className="p-1.5 hover:bg-gray-100 rounded-lg transition"
                  >
                    <X size={16} className="text-gray-500" />
                  </button>
                </div>
                
                {/* User Info */}
                <div className="p-4 border-b border-gray-100 bg-white">
                  <div className="flex items-center gap-3">
                    <img
                      src={profileImageUrl}
                      alt=""
                      className="w-10 h-10 rounded-full object-cover border border-gray-200"
                    />
                    <div className="flex-1 min-w-0">
                      <p className="text-sm font-semibold text-gray-900 truncate">{userDetails?.Name || "Driver User"}</p>
                      <p className="text-xs text-gray-500 truncate">{userDetails?.role || "Driver"}</p>
                      <p className="text-xs text-gray-400 truncate">ID: {userDetails?.ServiceNo || "N/A"}</p>
                    </div>
                  </div>
                </div>
                
                {/* Navigation */}
                <nav className="flex-1 p-2 space-y-1 overflow-y-auto">
                  {navItems.map((item, index) => (
                    <button
                      key={index}
                      onClick={() => handleNavigation(item.path)}
                      className={`w-full flex items-center gap-3 px-3 py-2.5 rounded-lg transition text-sm ${
                        isActive(item.path)
                          ? "bg-gray-900 text-white"
                          : "text-gray-700 hover:bg-gray-100"
                      }`}
                    >
                      <item.icon size={18} />
                      <span>{item.label}</span>
                    </button>
                  ))}
                </nav>
                
                {/* Footer */}
                <div className="p-4 border-t border-gray-100">
                  <div className="flex items-center justify-between mb-3 text-xs text-gray-500">
                    <span>Location Status</span>
                    <LocationIndicator />
                  </div>
                  <button
                    onClick={handleLogout}
                    disabled={loading}
                    className="w-full flex items-center justify-center gap-2 px-3 py-2.5 rounded-lg text-red-600 hover:bg-red-50 transition text-sm"
                  >
                    <LogOut size={16} />
                    <span>{loading ? "..." : "Logout"}</span>
                  </button>
                </div>
              </div>
            </motion.aside>
          </>
        )}
      </AnimatePresence>

      {/* ================= Main Content ================= */}
      <main
        className={`transition-all duration-300 ${
          sidebarOpen ? "md:pl-56" : "md:pl-16"
        }`}
        style={{ paddingTop: 'calc(48px + env(safe-area-inset-top))' }}
      >
        <div className="p-3 sm:p-4 md:p-6">
          {children}
        </div>
      </main>
    </div>
  );
};

export default DriverLayout;